import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Settings, X, ChevronDown, ChevronRight, Edit2 } from 'lucide-react';
import { useConfirm } from '../lib/ConfirmDialog';
import { useTranslation } from '../lib/i18n';
import { format, parseISO } from 'date-fns';
import { formatDate as fmt } from '../lib/dates';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Budget = ({ data, updateData }) => {
  const confirm = useConfirm();
  const { t, lang } = useTranslation();
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddSavings, setShowAddSavings] = useState(false);
  const [showIncomeInfo, setShowIncomeInfo] = useState(false);
  const [showExpenseInfo, setShowExpenseInfo] = useState(false);
  const [showSavingsInfo, setShowSavingsInfo] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showSubCategoryDetail, setShowSubCategoryDetail] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  const [newSubCategoryInputs, setNewSubCategoryInputs] = useState({});
  const [activeCategoryTab, setActiveCategoryTab] = useState(() => {
    // Load last opened tab from localStorage, default to 'expense'
    return localStorage.getItem('category_manager_last_tab') || 'expense';
  });
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#3b82f6');
  const [editingExpense, setEditingExpense] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [editingIncome, setEditingIncome] = useState(null);
  const [viewingIncome, setViewingIncome] = useState(null);
  const [editingSavings, setEditingSavings] = useState(null);
  const [viewingSavings, setViewingSavings] = useState(null);
  const [showCategoryPieChart, setShowCategoryPieChart] = useState(null);
  const [selectedPieSegment, setSelectedPieSegment] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const currentMonthStr = today.slice(0, 7); // "2026-04"

  // Month management - year/month tree structure
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return localStorage.getItem('budget_selected_month') || currentMonthStr;
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    return localStorage.getItem('budget_selected_year') || currentMonthStr.split('-')[0];
  });
  const [expandedYears, setExpandedYears] = useState(() => {
    const saved = localStorage.getItem('budget_expanded_years');
    return saved ? JSON.parse(saved) : { [currentMonthStr.split('-')[0]]: true };
  });
  const [showAddMonth, setShowAddMonth] = useState(false);
  const [newMonthInput, setNewMonthInput] = useState('');
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'year'
  // Phone-width flag: the category chart needs angled, shortened labels or
  // six category names collide into an unreadable mush.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsNarrow(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);
  const [newIncome, setNewIncome] = useState({ name: '', amount: '', category: '', subCategory: '', info: '', date: today });
  const [newExpense, setNewExpense] = useState({ name: '', amount: '', category: '', subCategory: '', info: '', date: today });
  const [newSavings, setNewSavings] = useState({ name: '', amount: '', accountId: '', info: '', date: today });

  // European formatting helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDateEuropean = (dateString) => fmt(dateString, 'long', lang) || dateString;

  const formatDateInput = (dateString) => {
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return format(date, "yyyy-MM-dd");
    } catch {
      return dateString;
    }
  };

  const parseEuropeanNumber = (value) => {
    if (!value || value === '') return 0;
    const str = String(value).trim();
    let cleaned;
    if (str.includes(',')) {
      // European format: dots are thousands separators, comma is decimal.
      // "1.234,56" -> 1234.56
      cleaned = str.replace(/\./g, '').replace(',', '.');
    } else if (str.includes('.')) {
      // No comma. A single dot followed by 1-2 digits is a decimal point
      // ("12.50" pasted from a banking app — stripping it would silently
      // store 1250, a 100x error). Anything else treats dots as thousands
      // separators ("1.234" -> 1234).
      cleaned = /^\d+\.\d{1,2}$/.test(str) ? str : str.replace(/\./g, '');
    } else {
      cleaned = str;
    }
    return parseFloat(cleaned) || 0;
  };

  const formatMonthDisplay = (monthStr) => {
    const [year, month] = String(monthStr).split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return fmt(date, 'monthYear', lang) || monthStr;
  };

  // Get all unique months from existing data
  const getAvailableMonths = () => {
    const allDates = [
      ...(data.budget.income || []).map(i => i.date),
      ...(data.budget.expenses || []).map(e => e.date),
      ...(data.budget.savings || []).map(s => s.date)
    ];
    const months = new Set(allDates.map(d => {
      try {
        const date = typeof d === 'string' ? parseISO(d) : new Date(d);
        return format(date, 'yyyy-MM');
      } catch {
        return null;
      }
    }).filter(Boolean));

    // Always include current month and selected month
    months.add(currentMonthStr);
    months.add(selectedMonth);

    return Array.from(months).sort();
  };

  const availableMonths = getAvailableMonths();

  // Filter data by selected month, or by selected year in year view
  const isInSelectedMonth = (dateStr) => {
    try {
      if (viewMode === 'year') {
        const itemYear = format(typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr), 'yyyy');
        return itemYear === String(selectedYear);
      }
      const itemMonth = format(typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr), 'yyyy-MM');
      return itemMonth === selectedMonth;
    } catch {
      return false;
    }
  };

  const filteredIncome = (data.budget.income || []).filter(i => isInSelectedMonth(i.date));
  const filteredExpenses = (data.budget.expenses || []).filter(e => isInSelectedMonth(e.date));
  const filteredSavings = (data.budget.savings || []).filter(s => isInSelectedMonth(s.date));

  const totalIncome = filteredIncome.reduce((sum, item) => sum + item.amount, 0);
  const totalExpenses = filteredExpenses.reduce((sum, item) => sum + item.amount, 0);
  const totalSavings = filteredSavings.reduce((sum, item) => sum + item.amount, 0);
  const balance = totalIncome - totalExpenses - totalSavings;

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setSelectedYear(month.split('-')[0]);
    setViewMode('month');
    localStorage.setItem('budget_selected_month', month);
    localStorage.setItem('budget_selected_year', month.split('-')[0]);
  };

  const handleYearView = (year) => {
    setSelectedYear(year);
    setViewMode('year');
    localStorage.setItem('budget_selected_year', year);
  };

  const toggleYearExpand = (year) => {
    const newExpanded = { ...expandedYears, [year]: !expandedYears[year] };
    setExpandedYears(newExpanded);
    localStorage.setItem('budget_expanded_years', JSON.stringify(newExpanded));
  };

  const addNewMonth = () => {
    if (!newMonthInput) return;
    const monthStr = newMonthInput.slice(0, 7); // Ensure format "2026-05"
    const year = monthStr.split('-')[0];
    // Expand the year when adding a month to it
    const newExpanded = { ...expandedYears, [year]: true };
    setExpandedYears(newExpanded);
    localStorage.setItem('budget_expanded_years', JSON.stringify(newExpanded));
    handleMonthSelect(monthStr);
    setNewMonthInput('');
    setShowAddMonth(false);
  };

  // Group months by year
  const getMonthsByYear = () => {
    const grouped = {};
    availableMonths.forEach(month => {
      const year = month.split('-')[0];
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(month);
    });
    return grouped;
  };

  const monthsByYear = getMonthsByYear();
  const availableYears = Object.keys(monthsByYear).sort().reverse();

  const addIncome = () => {
    if (newIncome.name && newIncome.amount) {
      const income = {
        id: Date.now().toString(),
        name: newIncome.name,
        amount: parseEuropeanNumber(newIncome.amount),
        category: newIncome.category,
        subCategory: newIncome.subCategory,
        info: newIncome.info,
        date: newIncome.date ? new Date(newIncome.date).toISOString() : new Date().toISOString()
      };
      updateData({
        budget: {
          ...data.budget,
          income: [...data.budget.income, income]
        }
      });
      setNewIncome({ name: '', amount: '', category: '', subCategory: '', info: '', date: today });
      setShowAddIncome(false);
      setShowIncomeInfo(false);
    }
  };

  const addExpense = () => {
    if (newExpense.name && newExpense.amount) {
      const expense = {
        id: Date.now().toString(),
        name: newExpense.name,
        amount: parseEuropeanNumber(newExpense.amount),
        category: newExpense.category,
        subCategory: newExpense.subCategory,
        info: newExpense.info,
        date: newExpense.date ? new Date(newExpense.date).toISOString() : new Date().toISOString()
      };
      updateData({
        budget: {
          ...data.budget,
          expenses: [...data.budget.expenses, expense]
        }
      });
      setNewExpense({ name: '', amount: '', category: 'other', subCategory: '', info: '', date: today });
      setShowAddExpense(false);
      setShowExpenseInfo(false);
    }
  };

  const deleteIncome = (id) => {
    updateData({
      budget: {
        ...data.budget,
        income: data.budget.income.filter(item => item.id !== id)
      }
    });
  };

  const deleteExpense = (id) => {
    updateData({
      budget: {
        ...data.budget,
        expenses: data.budget.expenses.filter(item => item.id !== id)
      }
    });
  };

  const updateExpense = () => {
    if (editingExpense && editingExpense.name && editingExpense.amount) {
      const updatedExpenses = data.budget.expenses.map(item =>
        item.id === editingExpense.id
          ? {
              ...item,
              name: editingExpense.name,
              amount: parseEuropeanNumber(editingExpense.amount),
              category: editingExpense.category,
              subCategory: editingExpense.subCategory,
              info: editingExpense.info,
              date: editingExpense.date ? new Date(editingExpense.date).toISOString() : item.date
            }
          : item
      );
      updateData({
        budget: {
          ...data.budget,
          expenses: updatedExpenses
        }
      });
      setEditingExpense(null);
    }
  };

  const updateIncome = () => {
    if (editingIncome && editingIncome.name && editingIncome.amount) {
      const updatedIncome = data.budget.income.map(item =>
        item.id === editingIncome.id
          ? {
              ...item,
              name: editingIncome.name,
              amount: parseEuropeanNumber(editingIncome.amount),
              category: editingIncome.category,
              subCategory: editingIncome.subCategory,
              info: editingIncome.info,
              date: editingIncome.date ? new Date(editingIncome.date).toISOString() : item.date
            }
          : item
      );
      updateData({
        budget: {
          ...data.budget,
          income: updatedIncome
        }
      });
      setEditingIncome(null);
    }
  };

  const updateSavings = () => {
    if (editingSavings && editingSavings.name && editingSavings.amount) {
      const updatedSavings = (data.budget.savings || []).map(item =>
        item.id === editingSavings.id
          ? {
              ...item,
              name: editingSavings.name,
              amount: parseEuropeanNumber(editingSavings.amount),
              accountId: editingSavings.accountId,
              info: editingSavings.info,
              date: editingSavings.date ? new Date(editingSavings.date).toISOString() : item.date
            }
          : item
      );
      updateData({
        budget: {
          ...data.budget,
          savings: updatedSavings
        }
      });
      setEditingSavings(null);
    }
  };

  const addSavings = () => {
    if (newSavings.name && newSavings.amount) {
      const savings = {
        id: Date.now().toString(),
        name: newSavings.name,
        amount: parseEuropeanNumber(newSavings.amount),
        accountId: newSavings.accountId,
        info: newSavings.info,
        date: newSavings.date ? new Date(newSavings.date).toISOString() : new Date().toISOString()
      };
      updateData({
        budget: {
          ...data.budget,
          savings: [...(data.budget.savings || []), savings]
        }
      });
      setNewSavings({ name: '', amount: '', accountId: '', info: '', date: today });
      setShowAddSavings(false);
      setShowSavingsInfo(false);
    }
  };

  const deleteSavings = (id) => {
    updateData({
      budget: {
        ...data.budget,
        savings: (data.budget.savings || []).filter(item => item.id !== id)
      }
    });
  };

  const toggleCategoryExpand = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const handleTabChange = (tab) => {
    setActiveCategoryTab(tab);
    localStorage.setItem('category_manager_last_tab', tab);
  };

  const getCurrentCategories = () => {
    if (activeCategoryTab === 'expense') {
      return data.budget.expenseCategories || data.budget.categories || [];
    } else if (activeCategoryTab === 'income') {
      return data.budget.incomeCategories || [];
    } else {
      return data.budget.savingsCategories || [];
    }
  };

  const getCategoryKey = () => {
    if (activeCategoryTab === 'expense') return 'expenseCategories';
    if (activeCategoryTab === 'income') return 'incomeCategories';
    return 'savingsCategories';
  };

  const addNewCategory = () => {
    if (!newCategoryName.trim()) return;
    const categoryKey = getCategoryKey();
    const currentCategories = getCurrentCategories();
    const newCategory = {
      id: Date.now().toString(),
      name: newCategoryName.trim(),
      color: newCategoryColor,
      subCategories: []
    };
    updateData({
      budget: { ...data.budget, [categoryKey]: [...currentCategories, newCategory] }
    });
    setNewCategoryName('');
    setNewCategoryColor('#3b82f6');
    setShowAddCategory(false);
  };

  const deleteCategory = (catId) => {
    const categoryKey = getCategoryKey();
    const currentCategories = getCurrentCategories();
    updateData({
      budget: { ...data.budget, [categoryKey]: currentCategories.filter(cat => cat.id !== catId) }
    });
  };

  const addSubCategory = (catId) => {
    const inputValue = newSubCategoryInputs[catId] || '';
    if (!inputValue.trim()) return;
    const categoryKey = getCategoryKey();
    const currentCategories = getCurrentCategories();
    const updatedCategories = currentCategories.map(cat =>
      cat.id === catId
        ? { ...cat, subCategories: [...(cat.subCategories || []), inputValue.trim()] }
        : cat
    );
    updateData({
      budget: { ...data.budget, [categoryKey]: updatedCategories }
    });
    setNewSubCategoryInputs(prev => ({ ...prev, [catId]: '' }));
  };

  const updateSubCategoryInput = (catId, value) => {
    setNewSubCategoryInputs(prev => ({ ...prev, [catId]: value }));
  };

  const removeSubCategory = (catId, subIndex) => {
    const categoryKey = getCategoryKey();
    const currentCategories = getCurrentCategories();
    const updatedCategories = currentCategories.map(cat =>
      cat.id === catId
        ? { ...cat, subCategories: cat.subCategories.filter((_, i) => i !== subIndex) }
        : cat
    );
    updateData({
      budget: { ...data.budget, [categoryKey]: updatedCategories }
    });
  };

  const getSubCategorySpending = (categoryId, subCategory) => {
    return data.budget.expenses
      .filter(e => e.category === categoryId && e.subCategory === subCategory)
      .reduce((sum, e) => sum + e.amount, 0);
  };

  const expenseCategoriesList = data.budget.expenseCategories || data.budget.categories || [];
  const incomeCategoriesList = data.budget.incomeCategories || [];
  const savingsCategoriesList = data.budget.savingsCategories || [];
  const savingsAccountsList = data.budget?.savingsAccounts || [];

  const chartData = expenseCategoriesList.map(cat => {
    const amount = filteredExpenses
      .filter(e => e.category === cat.id)
      .reduce((sum, e) => sum + e.amount, 0);
    return { name: cat.name, amount, color: cat.color, categoryId: cat.id };
  }).filter(item => item.amount > 0).sort((a, b) => b.amount - a.amount);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="space-y-6">
      {/* Header with Dropdown */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative min-w-0">
          {/* Dropdown Trigger */}
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-gray-900 hover:text-gray-700 transition-colors truncate max-w-full"
          >
            <span>
              {viewMode === 'year'
                ? t('budget.overviewYear', { year: selectedYear })
                : t('budget.titleMonth', { month: formatMonthDisplay(selectedMonth) })}
            </span>
            <ChevronDown
              size={24}
              className={`text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-96 overflow-auto">
              <div className="p-2 space-y-1">
                {availableYears.map(year => (
                  <div key={year}>
                    {/* Year Row */}
                    <button
                      onClick={() => {
                        handleYearView(year);
                        toggleYearExpand(year);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left font-medium transition-colors ${
                        viewMode === 'year' && selectedYear === year
                          ? 'bg-purple-100 text-purple-700'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span>{year}</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowAddMonth(year); }}
                          className="p-1 hover:bg-white rounded text-gray-400 hover:text-purple-600"
                          title={t('budget.addMonth')}
                        >
                          <Plus size={14} />
                        </button>
                        <span className="text-gray-400">
                          {expandedYears[year] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                      </div>
                    </button>

                    {/* Months Sub-menu */}
                    {expandedYears[year] && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
                        {monthsByYear[year]?.sort().reverse().map(month => (
                          <button
                            key={month}
                            onClick={() => {
                              handleMonthSelect(month);
                              setDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                              viewMode === 'month' && selectedMonth === month
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {formatMonthDisplay(month)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => setShowCategoryManager(true)}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          title={t('budget.manageCategories')}
        >
          <Settings size={18} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 relative z-0">
        <div className="card bg-green-50 border-green-200 min-w-0">
          <p className="text-green-600 text-xs sm:text-sm font-medium">{t('budget.totalIncome')}</p>
          <p className="text-lg sm:text-2xl font-bold text-green-700 truncate" title={formatCurrency(totalIncome)}>{formatCurrency(totalIncome)}</p>
        </div>
        <div className="card bg-red-50 border-red-200 min-w-0">
          <p className="text-red-600 text-xs sm:text-sm font-medium">{t('budget.totalExpenses')}</p>
          <p className="text-lg sm:text-2xl font-bold text-red-700 truncate" title={formatCurrency(totalExpenses)}>{formatCurrency(totalExpenses)}</p>
        </div>
        <div className="card bg-purple-50 border-purple-200 min-w-0">
          <p className="text-purple-600 text-xs sm:text-sm font-medium">{t('budget.totalSavings')}</p>
          <p className="text-lg sm:text-2xl font-bold text-purple-700 truncate" title={formatCurrency(totalSavings)}>{formatCurrency(totalSavings)}</p>
        </div>
        <div className={`card min-w-0 ${balance >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
          <p className={`text-xs sm:text-sm font-medium ${balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>{t('budget.remainingBalance')}</p>
          <p
            className={`text-lg sm:text-2xl font-bold truncate ${balance >= 0 ? 'text-blue-700' : 'text-orange-700'}`}
            title={formatCurrency(balance)}
          >
            {formatCurrency(balance)}
          </p>
        </div>
      </div>

      {/* Expense Chart */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">
          {viewMode === 'year'
            ? t('budget.yearlyExpensesByCategory')
            : t('budget.expensesByCategoryMonth', { month: formatMonthDisplay(selectedMonth) })}
        </h3>
        {chartData.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  height={isNarrow ? 78 : 60}
                  interval={0}
                  angle={isNarrow ? -35 : 0}
                  textAnchor={isNarrow ? 'end' : 'middle'}
                  tick={{ fill: '#374151', fontSize: isNarrow ? 10 : 14 }}
                  tickFormatter={(value) => {
                    if (isNarrow) {
                      // Angled labels still need a length cap on a phone.
                      return value.length > 13 ? `${value.slice(0, 12)}…` : value;
                    }
                    const words = value.split(' ');
                    if (words.length > 1 && value.length > 12) {
                      const mid = Math.ceil(words.length / 2);
                      return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')].join('\n');
                    }
                    return value;
                  }}
                />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar
                  dataKey="amount"
                  onClick={(data) => setShowCategoryPieChart(data)}
                  cursor="pointer"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">
            {viewMode === 'year'
              ? t('budget.noExpensesYear')
              : t('budget.noExpensesMonth', { month: formatMonthDisplay(selectedMonth) })}
          </p>
        )}
      </div>

      {/* Income, Expenses & Savings Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Income List */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t('budget.income')}</h3>
            <button
              onClick={() => { setNewIncome({ name: '', amount: '', category: '', subCategory: '', info: '', date: today }); setShowAddIncome(true); }}
              className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
              title={t('budget.addIncome')}
            >
              <Plus size={18} />
            </button>
          </div>
          {filteredIncome.length === 0 ? (
            <p className="text-gray-400 text-center py-8">
              {viewMode === 'year'
                ? t('budget.noIncomeYear')
                : t('budget.noIncomeMonth', { month: formatMonthDisplay(selectedMonth) })}
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-auto">
              {filteredIncome.slice().reverse().map((item) => (
                <div
                  key={item.id}
                  onClick={() => setViewingIncome(item)}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{item.name || item.source || t('budget.unnamed')}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatDateEuropean(item.date)}</span>
                      <span className="px-2 py-0.5 rounded" style={{ backgroundColor: incomeCategoriesList.find(c => c.id === item.category)?.color + '20', color: incomeCategoriesList.find(c => c.id === item.category)?.color }}>
                        {incomeCategoriesList.find(c => c.id === item.category)?.name}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-green-600">+{formatCurrency(item.amount)}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteIncome(item.id); }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Expenses List */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t('budget.expenses')}</h3>
            <button
              onClick={() => { setNewExpense({ name: '', amount: '', category: '', subCategory: '', info: '', date: today }); setShowAddExpense(true); }}
              className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
              title={t('budget.addExpense')}
            >
              <Plus size={18} />
            </button>
          </div>
          {filteredExpenses.length === 0 ? (
            <p className="text-gray-400 text-center py-8">
              {viewMode === 'year'
                ? t('budget.noExpensesYear')
                : t('budget.noExpensesMonth', { month: formatMonthDisplay(selectedMonth) })}
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-auto">
              {filteredExpenses.slice().reverse().map((item) => {
                const category = expenseCategoriesList.find(c => c.id === item.category);
                return (
                  <div
                    key={item.id}
                    onClick={() => setViewingExpense(item)}
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{item.name || item.description || t('budget.unnamed')}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{formatDateEuropean(item.date)}</span>
                        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: category?.color + '20', color: category?.color }}>
                          {category?.name}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-red-600">-{formatCurrency(item.amount)}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteExpense(item.id); }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Savings List */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t('budget.savings')}</h3>
            <button
              onClick={() => { setNewSavings({ name: '', amount: '', accountId: '', info: '', date: today }); setShowAddSavings(true); }}
              className="p-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-md transition-colors"
              title={t('budget.addSavings')}
            >
              <Plus size={18} />
            </button>
          </div>
          {filteredSavings.length === 0 ? (
            <p className="text-gray-400 text-center py-8">
              {viewMode === 'year'
                ? t('budget.noSavingsYear')
                : t('budget.noSavingsMonth', { month: formatMonthDisplay(selectedMonth) })}
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-auto">
              {filteredSavings.slice().reverse().map((item) => (
                <div
                  key={item.id}
                  onClick={() => setViewingSavings(item)}
                  className="flex items-center justify-between p-3 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{item.name || item.source || t('budget.unnamed')}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatDateEuropean(item.date)}</span>
                      <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#8b5cf6' + '20', color: '#8b5cf6' }}>
                        {savingsAccountsList.find(a => a.id === item.accountId)?.name || t('budget.unknownAccount')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-purple-600">+{formatCurrency(item.amount)}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSavings(item.id); }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Income Modal */}
      {showAddIncome && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('budget.addIncome')}</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder={t('budget.name')}
                className="input"
                value={newIncome.name}
                onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
              />
              <input
                type="text"
                placeholder={t('budget.amount')}
                className="input"
                value={newIncome.amount}
                onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value.replace(/[^0-9.,]/g, '') })}
                onBlur={(e) => {
                  const val = e.target.value.replace(/\./g, '').replace(',', '.');
                  const num = parseFloat(val);
                  if (!isNaN(num)) {
                    setNewIncome({ ...newIncome, amount: num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' });
                  }
                }}
              />
              <select
                className="input"
                value={newIncome.category}
                onChange={(e) => setNewIncome({ ...newIncome, category: e.target.value, subCategory: '' })}
              >
                <option value="">{t('budget.selectCategory')}</option>
                {incomeCategoriesList.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {(() => {
                const cat = newIncome.category ? incomeCategoriesList.find(c => c.id === newIncome.category) : null;
                return cat && cat.subCategories && cat.subCategories.length > 0;
              })() && (
                <select
                  className="input"
                  value={newIncome.subCategory}
                  onChange={(e) => setNewIncome({ ...newIncome, subCategory: e.target.value })}
                >
                  <option value="">{t('budget.selectSubCategory')}</option>
                  {(() => {
                    const cat = incomeCategoriesList.find(c => c.id === newIncome.category);
                    return cat?.subCategories?.map((sub, idx) => (
                      <option key={idx} value={sub}>{sub}</option>
                    ));
                  })()}
                </select>
              )}
              <input
                type="date"
                className="input"
                value={newIncome.date}
                onChange={(e) => setNewIncome({ ...newIncome, date: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowIncomeInfo(!showIncomeInfo)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {showIncomeInfo ? t('budget.hideInfo') : t('budget.addInfo')}
              </button>
              {showIncomeInfo && (
                <textarea
                  placeholder={t('budget.additionalInfoPlaceholder')}
                  className="input h-24 resize-none"
                  value={newIncome.info}
                  onChange={(e) => setNewIncome({ ...newIncome, info: e.target.value })}
                />
              )}
              <div className="flex gap-2">
                <button onClick={addIncome} className="btn-primary flex-1">{t('common.add')}</button>
                <button onClick={() => { setShowAddIncome(false); setShowIncomeInfo(false); setNewIncome({ name: '', amount: '', category: 'other_income', subCategory: '', info: '', date: today }); }} className="btn-secondary flex-1">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('budget.addExpense')}</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder={t('budget.name')}
                className="input"
                value={newExpense.name}
                onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
              />
              <input
                type="text"
                placeholder={t('budget.amount')}
                className="input"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value.replace(/[^0-9.,]/g, '') })}
                onBlur={(e) => {
                  const val = e.target.value.replace(/\./g, '').replace(',', '.');
                  const num = parseFloat(val);
                  if (!isNaN(num)) {
                    setNewExpense({ ...newExpense, amount: num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' });
                  }
                }}
              />
              <select
                className="input"
                value={newExpense.category}
                onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value, subCategory: '' })}
              >
                <option value="">{t('budget.selectCategory')}</option>
                {expenseCategoriesList.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {(() => {
                const cat = newExpense.category ? expenseCategoriesList.find(c => c.id === newExpense.category) : null;
                return cat && cat.subCategories && cat.subCategories.length > 0;
              })() && (
                <select
                  className="input"
                  value={newExpense.subCategory}
                  onChange={(e) => setNewExpense({ ...newExpense, subCategory: e.target.value })}
                >
                  <option value="">{t('budget.selectSubCategory')}</option>
                  {(() => {
                    const cat = expenseCategoriesList.find(c => c.id === newExpense.category);
                    return cat?.subCategories?.map((sub, idx) => (
                      <option key={idx} value={sub}>{sub}</option>
                    ));
                  })()}
                </select>
              )}
              <input
                type="date"
                className="input"
                value={newExpense.date}
                onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowExpenseInfo(!showExpenseInfo)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {showExpenseInfo ? t('budget.hideInfo') : t('budget.addInfo')}
              </button>
              {showExpenseInfo && (
                <textarea
                  placeholder={t('budget.additionalInfoPlaceholder')}
                  className="input h-24 resize-none"
                  value={newExpense.info}
                  onChange={(e) => setNewExpense({ ...newExpense, info: e.target.value })}
                />
              )}
              <div className="flex gap-2">
                <button onClick={addExpense} className="btn-primary flex-1">{t('common.add')}</button>
                <button onClick={() => { setShowAddExpense(false); setShowExpenseInfo(false); setNewExpense({ name: '', amount: '', category: 'other', subCategory: '', info: '', date: today }); }} className="btn-secondary flex-1">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Savings Modal */}
      {showAddSavings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('budget.addToSavings')}</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder={t('budget.name')}
                className="input"
                value={newSavings.name}
                onChange={(e) => setNewSavings({ ...newSavings, name: e.target.value })}
              />
              <input
                type="text"
                placeholder={t('budget.amount')}
                className="input"
                value={newSavings.amount}
                onChange={(e) => setNewSavings({ ...newSavings, amount: e.target.value.replace(/[^0-9.,]/g, '') })}
                onBlur={(e) => {
                  const val = e.target.value.replace(/\./g, '').replace(',', '.');
                  const num = parseFloat(val);
                  if (!isNaN(num)) {
                    setNewSavings({ ...newSavings, amount: num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' });
                  }
                }}
              />
              <select
                className="input"
                value={newSavings.accountId || ''}
                onChange={(e) => setNewSavings({ ...newSavings, accountId: e.target.value })}
              >
                <option value="">{t('budget.selectSavingsAccount')}</option>
                {savingsAccountsList.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name} ({formatCurrency(acc.balance)})</option>
                ))}
              </select>
              {savingsAccountsList.length === 0 && (
                <p className="text-sm text-orange-500">
                  {t('budget.noSavingsAccounts')}
                </p>
              )}
              <input
                type="date"
                className="input"
                value={newSavings.date}
                onChange={(e) => setNewSavings({ ...newSavings, date: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowSavingsInfo(!showSavingsInfo)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {showSavingsInfo ? t('budget.hideInfo') : t('budget.addInfo')}
              </button>
              {showSavingsInfo && (
                <textarea
                  placeholder={t('budget.additionalInfoPlaceholder')}
                  className="input h-24 resize-none"
                  value={newSavings.info}
                  onChange={(e) => setNewSavings({ ...newSavings, info: e.target.value })}
                />
              )}
              <div className="flex gap-2">
                <button onClick={addSavings} className="btn bg-purple-600 text-white hover:bg-purple-700 flex-1">{t('common.add')}</button>
                <button onClick={() => { setShowAddSavings(false); setShowSavingsInfo(false); setNewSavings({ name: '', amount: '', accountId: '', info: '', date: today }); }} className="btn-secondary flex-1">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('budget.manageCategories')}</h3>
              <button
                onClick={() => setShowCategoryManager(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 border-b border-gray-200">
              <button
                onClick={() => handleTabChange('expense')}
                className={`px-4 py-2 font-medium text-sm transition-colors ${
                  activeCategoryTab === 'expense'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('budget.tabExpense')}
              </button>
              <button
                onClick={() => handleTabChange('income')}
                className={`px-4 py-2 font-medium text-sm transition-colors ${
                  activeCategoryTab === 'income'
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('budget.tabIncome')}
              </button>
              <button
                onClick={() => handleTabChange('savings')}
                className={`px-4 py-2 font-medium text-sm transition-colors ${
                  activeCategoryTab === 'savings'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('budget.tabSavings')}
              </button>
            </div>

            {/* Add New Category Button */}
            <button
              onClick={() => { setNewCategoryName(''); setNewCategoryColor('#3b82f6'); setShowAddCategory(true); }}
              className="w-full mb-4 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={18} />
              {t(activeCategoryTab === 'expense' ? 'budget.addNewExpenseCategory' : activeCategoryTab === 'income' ? 'budget.addNewIncomeCategory' : 'budget.addNewSavingsCategory')}
            </button>

            <div className="space-y-3">
              {getCurrentCategories().map((cat) => (
                <div key={cat.id} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleCategoryExpand(cat.id)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                      <span className="font-medium">{cat.name}</span>
                      <span className="text-xs text-gray-400">
                        {t('budget.subCategoriesCount', { count: cat.subCategories?.length || 0 })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {activeCategoryTab === 'expense' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowSubCategoryDetail(cat); }}
                          className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
                        >
                          {t('budget.viewDetails')}
                        </button>
                      )}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (await confirm({ message: t('budget.deleteCategoryConfirm', { name: cat.name }), danger: true })) deleteCategory(cat.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title={t('budget.deleteCategory')}
                      >
                        <Trash2 size={14} />
                      </button>
                      {expandedCategories[cat.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </div>
                  </button>
                  {expandedCategories[cat.id] && (
                    <div className="p-3 pt-0 border-t border-gray-100">
                      <div className="mt-2 space-y-2">
                        {cat.subCategories?.map((sub, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm pl-4">
                            <span>{sub}</span>
                            <button
                              onClick={() => removeSubCategory(cat.id, idx)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            placeholder={t('budget.newSubCategoryPlaceholder')}
                            className="input text-sm flex-1"
                            value={newSubCategoryInputs[cat.id] || ''}
                            onChange={(e) => updateSubCategoryInput(cat.id, e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addSubCategory(cat.id)}
                          />
                          <button
                            onClick={() => addSubCategory(cat.id)}
                            className="btn-primary text-sm px-3"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add New Category Form */}
            {showAddCategory && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium mb-3">{t(activeCategoryTab === 'expense' ? 'budget.addNewExpenseCategory' : activeCategoryTab === 'income' ? 'budget.addNewIncomeCategory' : 'budget.addNewSavingsCategory')}</h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder={t('budget.categoryNamePlaceholder')}
                    className="input"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addNewCategory()}
                  />
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-600">{t('budget.colorLabel')}</label>
                    <input
                      type="color"
                      value={newCategoryColor}
                      onChange={(e) => setNewCategoryColor(e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-500">{newCategoryColor}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={addNewCategory} className="btn-primary flex-1">{t('budget.addCategory')}</button>
                    <button onClick={() => { setShowAddCategory(false); setNewCategoryName(''); setNewCategoryColor('#3b82f6'); }} className="btn-secondary flex-1">{t('common.cancel')}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Month Modal */}
      {showAddMonth && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('budget.addMonthToYear', { year: showAddMonth })}</h3>
              <button
                onClick={() => { setShowAddMonth(false); setNewMonthInput(''); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 mb-2 block">{t('budget.selectMonth')}</label>
                <input
                  type="month"
                  className="input w-full"
                  value={newMonthInput}
                  onChange={(e) => setNewMonthInput(e.target.value)}
                  placeholder="YYYY-MM"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={addNewMonth}
                  disabled={!newMonthInput}
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {t('budget.addMonth')}
                </button>
                <button
                  onClick={() => { setShowAddMonth(false); setNewMonthInput(''); }}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Category Detail Popup */}
      {showSubCategoryDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: showSubCategoryDetail.color }} />
                <h3 className="text-lg font-semibold">{t('budget.categoryDetails', { name: showSubCategoryDetail.name })}</h3>
              </div>
              <button
                onClick={() => setShowSubCategoryDetail(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              {showSubCategoryDetail.subCategories?.map((sub, idx) => {
                const spending = getSubCategorySpending(showSubCategoryDetail.id, sub);
                return (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{sub}</span>
                    <span className={`font-semibold ${spending > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {formatCurrency(spending)}
                    </span>
                  </div>
                );
              })}
              {(!showSubCategoryDetail.subCategories || showSubCategoryDetail.subCategories.length === 0) && (
                <p className="text-gray-400 text-center py-4">{t('budget.noSubCategories')}</p>
              )}
            </div>
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between font-semibold text-lg">
                <span>{t('budget.totalCategory')}</span>
                <span className="text-red-600">
                  {formatCurrency(data.budget.expenses
                    .filter(e => e.category === showSubCategoryDetail.id)
                    .reduce((sum, e) => sum + e.amount, 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Detail View Modal */}
      {viewingExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('budget.expenseDetails')}</h3>
              <button
                onClick={() => setViewingExpense(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">{t('budget.name')}</p>
                <p className="font-medium text-lg">{viewingExpense.name || t('budget.unnamed')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.amount')}</p>
                <p className="font-semibold text-xl text-red-600">{formatCurrency(viewingExpense.amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('common.date')}</p>
                <p className="font-medium">{formatDateEuropean(viewingExpense.date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.category')}</p>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: expenseCategoriesList.find(c => c.id === viewingExpense.category)?.color }}
                  />
                  <span className="font-medium">
                    {expenseCategoriesList.find(c => c.id === viewingExpense.category)?.name || t('budget.unknownCategory')}
                  </span>
                </div>
              </div>
              {viewingExpense.subCategory && (
                <div>
                  <p className="text-sm text-gray-500">{t('budget.subCategory')}</p>
                  <p className="font-medium">{viewingExpense.subCategory}</p>
                </div>
              )}
              {viewingExpense.info && (
                <div>
                  <p className="text-sm text-gray-500">{t('budget.additionalInfo')}</p>
                  <p className="font-medium text-gray-700 bg-gray-50 p-3 rounded-lg">{viewingExpense.info}</p>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => {
                    const amount = viewingExpense.amount;
                    const formattedAmount = amount ? amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '';
                    setEditingExpense({
                      ...viewingExpense,
                      amount: formattedAmount,
                      date: formatDateInput(viewingExpense.date)
                    });
                    setViewingExpense(null);
                  }}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <Edit2 size={18} />
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => setViewingExpense(null)}
                  className="btn-secondary flex-1"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Expense Modal */}
      {editingExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('budget.editExpense')}</h3>
              <button
                onClick={() => setEditingExpense(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder={t('budget.name')}
                className="input"
                value={editingExpense.name || ''}
                onChange={(e) => setEditingExpense({ ...editingExpense, name: e.target.value })}
              />
              <input
                type="text"
                placeholder={t('budget.amount')}
                className="input"
                value={editingExpense.amount || ''}
                onChange={(e) => setEditingExpense({ ...editingExpense, amount: e.target.value.replace(/[^0-9.,]/g, '') })}
                onBlur={(e) => {
                  const val = e.target.value.replace(/\./g, '').replace(',', '.');
                  const num = parseFloat(val);
                  if (!isNaN(num)) {
                    setEditingExpense({ ...editingExpense, amount: num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' });
                  }
                }}
              />
              <select
                className="input"
                value={editingExpense.category || 'other'}
                onChange={(e) => setEditingExpense({ ...editingExpense, category: e.target.value, subCategory: '' })}
              >
                {expenseCategoriesList.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {(() => {
                const cat = expenseCategoriesList.find(c => c.id === editingExpense.category);
                const hasSubs = cat && cat.subCategories && cat.subCategories.length > 0;
                return hasSubs;
              })() && (
                <select
                  className="input"
                  value={editingExpense.subCategory || ''}
                  onChange={(e) => setEditingExpense({ ...editingExpense, subCategory: e.target.value })}
                >
                  <option value="">{t('budget.selectSubCategory')}</option>
                  {(() => {
                    const cat = expenseCategoriesList.find(c => c.id === editingExpense.category);
                    return cat?.subCategories?.map((sub, idx) => (
                      <option key={idx} value={sub}>{sub}</option>
                    ));
                  })()}
                </select>
              )}
              <input
                type="date"
                className="input"
                value={editingExpense.date || ''}
                onChange={(e) => setEditingExpense({ ...editingExpense, date: e.target.value })}
              />
              <textarea
                placeholder={t('budget.additionalInfoPlaceholder')}
                className="input h-24 resize-none"
                value={editingExpense.info || ''}
                onChange={(e) => setEditingExpense({ ...editingExpense, info: e.target.value })}
              />
              <div className="flex gap-2">
                <button onClick={updateExpense} className="btn-primary flex-1">{t('budget.saveChanges')}</button>
                <button onClick={() => setEditingExpense(null)} className="btn-secondary flex-1">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Income Detail View Modal */}
      {viewingIncome && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('budget.incomeDetails')}</h3>
              <button
                onClick={() => setViewingIncome(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">{t('budget.name')}</p>
                <p className="font-medium text-lg">{viewingIncome.name || t('budget.unnamed')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.amount')}</p>
                <p className="font-semibold text-xl text-green-600">{formatCurrency(viewingIncome.amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('common.date')}</p>
                <p className="font-medium">{formatDateEuropean(viewingIncome.date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.category')}</p>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: incomeCategoriesList.find(c => c.id === viewingIncome.category)?.color }}
                  />
                  <span className="font-medium">
                    {incomeCategoriesList.find(c => c.id === viewingIncome.category)?.name || t('budget.unknownCategory')}
                  </span>
                </div>
              </div>
              {viewingIncome.subCategory && (
                <div>
                  <p className="text-sm text-gray-500">{t('budget.subCategory')}</p>
                  <p className="font-medium">{viewingIncome.subCategory}</p>
                </div>
              )}
              {viewingIncome.info && (
                <div>
                  <p className="text-sm text-gray-500">{t('budget.additionalInfo')}</p>
                  <p className="font-medium text-gray-700 bg-gray-50 p-3 rounded-lg">{viewingIncome.info}</p>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => {
                    const amount = viewingIncome.amount;
                    const formattedAmount = amount ? amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '';
                    setEditingIncome({
                      ...viewingIncome,
                      amount: formattedAmount,
                      date: formatDateInput(viewingIncome.date)
                    });
                    setViewingIncome(null);
                  }}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <Edit2 size={18} />
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => setViewingIncome(null)}
                  className="btn-secondary flex-1"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Income Modal */}
      {editingIncome && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('budget.editIncome')}</h3>
              <button
                onClick={() => setEditingIncome(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder={t('budget.name')}
                className="input"
                value={editingIncome.name || ''}
                onChange={(e) => setEditingIncome({ ...editingIncome, name: e.target.value })}
              />
              <input
                type="text"
                placeholder={t('budget.amount')}
                className="input"
                value={editingIncome.amount || ''}
                onChange={(e) => setEditingIncome({ ...editingIncome, amount: e.target.value.replace(/[^0-9.,]/g, '') })}
                onBlur={(e) => {
                  const val = e.target.value.replace(/\./g, '').replace(',', '.');
                  const num = parseFloat(val);
                  if (!isNaN(num)) {
                    setEditingIncome({ ...editingIncome, amount: num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' });
                  }
                }}
              />
              <select
                className="input"
                value={editingIncome.category || 'other_income'}
                onChange={(e) => setEditingIncome({ ...editingIncome, category: e.target.value, subCategory: '' })}
              >
                {incomeCategoriesList.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {(() => {
                const cat = incomeCategoriesList.find(c => c.id === editingIncome.category);
                const hasSubs = cat && cat.subCategories && cat.subCategories.length > 0;
                return hasSubs;
              })() && (
                <select
                  className="input"
                  value={editingIncome.subCategory || ''}
                  onChange={(e) => setEditingIncome({ ...editingIncome, subCategory: e.target.value })}
                >
                  <option value="">{t('budget.selectSubCategory')}</option>
                  {(() => {
                    const cat = incomeCategoriesList.find(c => c.id === editingIncome.category);
                    return cat?.subCategories?.map((sub, idx) => (
                      <option key={idx} value={sub}>{sub}</option>
                    ));
                  })()}
                </select>
              )}
              <input
                type="date"
                className="input"
                value={editingIncome.date || ''}
                onChange={(e) => setEditingIncome({ ...editingIncome, date: e.target.value })}
              />
              <textarea
                placeholder={t('budget.additionalInfoPlaceholder')}
                className="input h-24 resize-none"
                value={editingIncome.info || ''}
                onChange={(e) => setEditingIncome({ ...editingIncome, info: e.target.value })}
              />
              <div className="flex gap-2">
                <button onClick={updateIncome} className="btn-primary flex-1">{t('budget.saveChanges')}</button>
                <button onClick={() => setEditingIncome(null)} className="btn-secondary flex-1">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Savings Detail View Modal */}
      {viewingSavings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('budget.savingsDetails')}</h3>
              <button
                onClick={() => setViewingSavings(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">{t('budget.name')}</p>
                <p className="font-medium text-lg">{viewingSavings.name || t('budget.unnamed')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.amount')}</p>
                <p className="font-semibold text-xl text-purple-600">{formatCurrency(viewingSavings.amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('common.date')}</p>
                <p className="font-medium">{formatDateEuropean(viewingSavings.date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('budget.account')}</p>
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {savingsAccountsList.find(a => a.id === viewingSavings.accountId)?.name || t('budget.unknownAccount')}
                  </span>
                </div>
              </div>
              {viewingSavings.info && (
                <div>
                  <p className="text-sm text-gray-500">{t('budget.additionalInfo')}</p>
                  <p className="font-medium text-gray-700 bg-gray-50 p-3 rounded-lg">{viewingSavings.info}</p>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => {
                    const amount = viewingSavings.amount;
                    const formattedAmount = amount ? amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '';
                    setEditingSavings({
                      ...viewingSavings,
                      amount: formattedAmount,
                      date: formatDateInput(viewingSavings.date)
                    });
                    setViewingSavings(null);
                  }}
                  className="btn bg-purple-600 text-white hover:bg-purple-700 flex-1 flex items-center justify-center gap-2"
                >
                  <Edit2 size={18} />
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => setViewingSavings(null)}
                  className="btn-secondary flex-1"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Savings Modal */}
      {editingSavings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('budget.editSavings')}</h3>
              <button
                onClick={() => setEditingSavings(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder={t('budget.name')}
                className="input"
                value={editingSavings.name || ''}
                onChange={(e) => setEditingSavings({ ...editingSavings, name: e.target.value })}
              />
              <input
                type="text"
                placeholder={t('budget.amount')}
                className="input"
                value={editingSavings.amount || ''}
                onChange={(e) => setEditingSavings({ ...editingSavings, amount: e.target.value.replace(/[^0-9.,]/g, '') })}
                onBlur={(e) => {
                  const val = e.target.value.replace(/\./g, '').replace(',', '.');
                  const num = parseFloat(val);
                  if (!isNaN(num)) {
                    setEditingSavings({ ...editingSavings, amount: num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' });
                  }
                }}
              />
              <select
                className="input"
                value={editingSavings.accountId || ''}
                onChange={(e) => setEditingSavings({ ...editingSavings, accountId: e.target.value })}
              >
                <option value="">{t('budget.selectSavingsAccount')}</option>
                {savingsAccountsList.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name} ({formatCurrency(acc.balance)})</option>
                ))}
              </select>
              {savingsAccountsList.length === 0 && (
                <p className="text-sm text-orange-500">
                  {t('budget.noSavingsAccounts')}
                </p>
              )}
              <input
                type="date"
                className="input"
                value={editingSavings.date || ''}
                onChange={(e) => setEditingSavings({ ...editingSavings, date: e.target.value })}
              />
              <textarea
                placeholder={t('budget.additionalInfoPlaceholder')}
                className="input h-24 resize-none"
                value={editingSavings.info || ''}
                onChange={(e) => setEditingSavings({ ...editingSavings, info: e.target.value })}
              />
              <div className="flex gap-2">
                <button onClick={updateSavings} className="btn bg-purple-600 text-white hover:bg-purple-700 flex-1">{t('budget.saveChanges')}</button>
                <button onClick={() => setEditingSavings(null)} className="btn-secondary flex-1">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Pie Chart Modal */}
      {showCategoryPieChart && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: showCategoryPieChart.color }}>
                {t('budget.categorySubcategories', { name: showCategoryPieChart.name })}
              </h3>
              <button
                onClick={() => { setShowCategoryPieChart(null); setSelectedPieSegment(null); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex gap-4">
              {/* Left side - Selected segment info */}
              <div className="w-40 flex-shrink-0">
                {selectedPieSegment ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div
                      className="w-4 h-4 rounded-full mb-2"
                      style={{ backgroundColor: selectedPieSegment.color }}
                    />
                    <p className="font-semibold text-sm text-gray-800">{selectedPieSegment.name}</p>
                    <p className="text-lg font-bold" style={{ color: showCategoryPieChart.color }}>
                      {formatCurrency(selectedPieSegment.value)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {((selectedPieSegment.value / selectedPieSegment.total) * 100).toFixed(1)}%
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 text-gray-400 text-sm">
                    {t('budget.pieHint')}
                  </div>
                )}
              </div>
              {/* Right side - Chart */}
              <div className="flex-1 h-72 relative">
                {(() => {
                  const category = expenseCategoriesList.find(c => c.id === showCategoryPieChart.categoryId);
                  const subCategories = category?.subCategories || [];
                  const pieData = subCategories.map(sub => {
                    const amount = filteredExpenses
                      .filter(e => e.category === showCategoryPieChart.categoryId && e.subCategory === sub)
                      .reduce((sum, e) => sum + e.amount, 0);
                    return { name: sub, value: amount };
                  }).filter(d => d.value > 0);

                  // Add "Other" for expenses without subcategory
                  const otherAmount = filteredExpenses
                    .filter(e => e.category === showCategoryPieChart.categoryId && (!e.subCategory || e.subCategory === ''))
                    .reduce((sum, e) => sum + e.amount, 0);
                  if (otherAmount > 0) {
                    pieData.push({ name: t('budget.otherSubCategory'), value: otherAmount });
                  }

                  const total = pieData.reduce((sum, d) => sum + d.value, 0);

                  if (pieData.length === 0) {
                    return (
                      <div className="h-full flex items-center justify-center text-gray-400">
                        {t('budget.noData')}
                      </div>
                    );
                  }

                  const colors = ['#3b82f6', '#22c55e', '#f97316', '#8b5cf6', '#ef4444', '#06b6d4', '#eab308', '#64748b'];

                  const handleSegmentHover = (entry, index) => {
                    setSelectedPieSegment({
                      name: entry.name,
                      value: entry.value,
                      total: total,
                      color: colors[index % 8]
                    });
                  };

                  return (
                    <>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            dataKey="value"
                            stroke="#fff"
                            strokeWidth={3}
                            onMouseEnter={(_, index) => handleSegmentHover(pieData[index], index)}
                            onClick={(_, index) => handleSegmentHover(pieData[index], index)}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={colors[index % 8]} cursor="pointer" />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      {/* Total in center */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">{t('budget.total')}</p>
                          <p className="text-lg font-bold" style={{ color: showCategoryPieChart.color }}>
                            {formatCurrency(total)}
                          </p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-3 pt-4 mt-2">
              {(() => {
                const category = expenseCategoriesList.find(c => c.id === showCategoryPieChart.categoryId);
                const subCategories = category?.subCategories || [];
                const pieData = subCategories.map(sub => {
                  const amount = filteredExpenses
                    .filter(e => e.category === showCategoryPieChart.categoryId && e.subCategory === sub)
                    .reduce((sum, e) => sum + e.amount, 0);
                  return { name: sub, value: amount };
                }).filter(d => d.value > 0);
                const otherAmount = filteredExpenses
                  .filter(e => e.category === showCategoryPieChart.categoryId && (!e.subCategory || e.subCategory === ''))
                  .reduce((sum, e) => sum + e.amount, 0);
                if (otherAmount > 0) {
                  pieData.push({ name: t('budget.otherSubCategory'), value: otherAmount });
                }
                const colors = ['#3b82f6', '#22c55e', '#f97316', '#8b5cf6', '#ef4444', '#06b6d4', '#eab308', '#64748b'];
                return pieData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center gap-1.5">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: colors[index % 8] }}
                    />
                    <span className="text-xs text-gray-600">{entry.name}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budget;
